import { Copy, Check, PencilSimple, CaretDown, CaretUp, ArrowsClockwise, SpinnerGap } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";

export function SectionCard({
  sectionName, idx, content,
  isCollapsed, onToggleCollapse,
  isEditing, editedValue, onStartEdit, onSaveEdit, onDiscardEdit, onEditChange,
  isCopied, onCopy,
  aiModel, generationContext,
  regenOpen, onToggleRegen, regenInstruction, onRegenInstructionChange,
  regenLoading, regenSuccess, regenError, onRegenerate,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden"
      data-testid={`section-${idx}`}
    >
      {/* Section header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
        <div className="flex items-center gap-3">
          <span className="font-['JetBrains_Mono'] text-xs text-[var(--success)] opacity-60">
            {String(idx + 1).padStart(2, "0")}
          </span>
          <h3 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[#FF4400]">
            {sectionName}
          </h3>
        </div>
        <div className="flex items-center gap-1">
          {aiModel && generationContext && (
            <button
              onClick={() => onToggleRegen(sectionName)}
              disabled={regenLoading}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--accent-subtle)] transition-colors disabled:opacity-40"
              data-testid={`regenerate-section-btn-${idx}`}
            >
              {regenLoading ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              Regenerate
            </button>
          )}
          {regenSuccess && (
            <span className="w-2 h-2 rounded-full bg-[var(--success)] animate-pulse" data-testid={`regen-success-${idx}`} />
          )}
          <button onClick={() => onCopy(sectionName)}
            className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" title="Copy section">
            {isCopied ? <Check size={16} className="text-[var(--success)]" /> : <Copy size={16} />}
          </button>
          <button onClick={() => isEditing ? onSaveEdit(sectionName) : onStartEdit(sectionName)}
            className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" title="Edit section">
            <PencilSimple size={16} className={isEditing ? "text-[#FF4400]" : ""} />
          </button>
          <button onClick={() => onToggleCollapse(sectionName)}
            className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors">
            {isCollapsed ? <CaretDown size={16} /> : <CaretUp size={16} />}
          </button>
        </div>
      </div>

      {/* Inline regenerate input */}
      {regenOpen && !regenLoading && (
        <div className="px-6 py-3 bg-[var(--bg-base)] border-b border-[var(--border)] space-y-2" data-testid={`regen-input-${idx}`}>
          <input type="text" value={regenInstruction} onChange={(e) => onRegenInstructionChange(e.target.value)}
            placeholder="Optional: any specific direction? (leave blank to just regenerate)"
            className="w-full bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
            data-testid={`regen-instruction-${idx}`}
            onKeyDown={(e) => { if (e.key === "Enter") onRegenerate(sectionName); }} />
          <div className="flex gap-2">
            <button onClick={() => onRegenerate(sectionName)}
              className="px-3 py-1.5 text-xs bg-[#FF4400] text-white rounded-md hover:bg-[#FF6633] transition-colors"
              data-testid={`regen-confirm-${idx}`}>Regenerate</button>
            <button onClick={() => onToggleRegen(null)}
              className="px-3 py-1.5 text-xs bg-[var(--bg-raised)] text-[var(--text-secondary)] rounded-md border border-[var(--border-strong)] hover:text-[var(--text-primary)] transition-colors"
              data-testid={`regen-cancel-${idx}`}>Cancel</button>
          </div>
        </div>
      )}

      {/* Section content */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-6 py-5">
              {regenLoading ? (
                <div className="space-y-3 animate-pulse" data-testid={`regen-loading-${idx}`}>
                  <div className="h-4 bg-[var(--bg-hover)] rounded w-full" />
                  <div className="h-4 bg-[var(--bg-hover)] rounded w-5/6" />
                  <div className="h-4 bg-[var(--bg-hover)] rounded w-4/6" />
                  <div className="h-4 bg-[var(--bg-hover)] rounded w-full" />
                  <div className="h-4 bg-[var(--bg-hover)] rounded w-3/4" />
                </div>
              ) : isEditing ? (
                <div className="space-y-2">
                  <textarea value={editedValue || ""}
                    onChange={(e) => onEditChange(sectionName, e.target.value)}
                    className="w-full bg-[var(--bg-base)] border border-[#FF4400]/30 rounded-md p-4 text-[var(--text-primary)] text-sm leading-relaxed resize-y min-h-[200px] focus:outline-none focus:ring-1 focus:ring-[#FF4400] font-['Manrope']"
                    data-testid={`edit-section-${idx}`} />
                  <div className="flex gap-2">
                    <button onClick={() => onSaveEdit(sectionName)}
                      className="px-3 py-1.5 text-xs bg-[#FF4400] text-white rounded-md hover:bg-[#FF6633] transition-colors">Done Editing</button>
                    <button onClick={() => onDiscardEdit(sectionName)}
                      className="px-3 py-1.5 text-xs bg-[var(--bg-raised)] text-[var(--text-secondary)] rounded-md border border-[var(--border-strong)] hover:text-[var(--text-primary)] transition-colors">
                      Discard Changes
                    </button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none text-[var(--text-secondary)] leading-relaxed
                  prose-headings:text-[var(--text-primary)] prose-headings:font-['Cormorant_Garamond'] prose-headings:font-semibold
                  prose-h1:text-xl prose-h2:text-lg prose-h3:text-base
                  prose-p:text-sm prose-p:leading-relaxed prose-p:text-[var(--text-secondary)]
                  prose-strong:text-[var(--text-primary)] prose-strong:font-semibold
                  prose-em:text-[var(--text-secondary)]
                  prose-ul:text-sm prose-ol:text-sm prose-li:text-[var(--text-secondary)]
                  prose-a:text-[#FF4400] prose-a:no-underline hover:prose-a:underline
                  prose-hr:border-[var(--border)]
                  prose-blockquote:border-l-[#FF4400] prose-blockquote:text-[var(--text-muted)] prose-blockquote:font-normal
                  prose-code:text-[#FF4400] prose-code:bg-[var(--bg-base)] prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs
                  prose-pre:bg-[var(--bg-base)] prose-pre:border prose-pre:border-[var(--border)]"
                  data-testid={`section-content-${idx}`}>
                  <ReactMarkdown>{content}</ReactMarkdown>
                </div>
              )}
              {regenError && (
                <p className="text-xs text-red-400 mt-2" data-testid={`regen-error-${idx}`}>{regenError}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
